/**
 * Memoised hex → rgba strings for Jump draw modules. The palette is tiny per
 * chart so an uncapped Map stays in single-digit-entry territory while
 * avoiding regex + parseInt work on hot paths.
 */

export interface RgbTuple {
  r: number;
  g: number;
  b: number;
}

const rgbCache = new Map<string, RgbTuple | null>();
const rgbaCache = new Map<string, string>();

function parseHexRgb(hex: string): RgbTuple | null {
  const cached = rgbCache.get(hex);
  if (cached !== undefined) {
    return cached;
  }
  const normalised = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalised)) {
    rgbCache.set(hex, null);
    return null;
  }
  const parsed: RgbTuple = {
    r: parseInt(normalised.slice(0, 2), 16),
    g: parseInt(normalised.slice(2, 4), 16),
    b: parseInt(normalised.slice(4, 6), 16),
  };
  rgbCache.set(hex, parsed);
  return parsed;
}

/** Parse a `#RRGGBB` hex to its r/g/b channels (memoised), or null if malformed. */
export function hexToRgb(hex: string): RgbTuple | null {
  return parseHexRgb(hex);
}

export function hexToRgba(hex: string, alpha: number): string {
  const key = `${hex}|${alpha}`;
  const cached = rgbaCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const rgb = parseHexRgb(hex);
  const value = rgb
    ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
    : `rgba(255, 255, 255, ${alpha})`;
  rgbaCache.set(key, value);
  return value;
}
